// src/features/settings/DeleteAccountDialog.tsx
import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button,
    TextField, Box, CircularProgress, Alert
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { deleteAccountThunk, clearAuthStatus } from '../auth/authSlice';
import { useTranslation } from 'react-i18next'; // NEW: Import useTranslation hook for i18n
import { Trans } from 'react-i18next'; // NEW: Import Trans component for handling HTML tags

interface DeleteAccountDialogProps {
    open: boolean;
    onClose: () => void;
}

const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({ open, onClose }) => {
    const dispatch = useAppDispatch();
    const { status, error } = useAppSelector(state => state.auth);
    const [confirmationText, setConfirmationText] = useState('');
    const { t } = useTranslation(); // NEW: Initialize translation hook

    const handleDelete = () => {
        dispatch(clearAuthStatus()); // Clear previous errors
        dispatch(deleteAccountThunk());
        // The redirect logic will be handled by a listener in App.tsx or ProtectedRoute
    };

    const isConfirmationMatch = confirmationText === 'DELETE';
    const isLoading = status.auth === 'loading';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('delete_account_dialog_title')}</DialogTitle> {/* NEW: Use translation key for title */}
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <DialogContentText color="error.main" fontWeight="bold">
                    {t('delete_account_warning_irreversible')} {/* NEW: Use translation key for warning */}
                </DialogContentText>
                <DialogContentText sx={{ mt: 1 }}>
                    {t('delete_account_description')} {/* NEW: Use translation key for description */}
                </DialogContentText>
                <DialogContentText sx={{ mt: 2 }}>
                    <Trans i18nKey="delete_account_confirmation_prompt">
                        To confirm, please type <strong>DELETE</strong> in the box below.
                    </Trans> {/* NEW: Use Trans for text with <strong> tag */}
                </DialogContentText>
                <TextField
                    autoFocus
                    margin="dense"
                    id="confirmation"
                    label={t('delete_account_confirmation_label')} // NEW: Use translation key for label
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={confirmationText}
                    onChange={(e) => setConfirmationText(e.target.value)}
                    disabled={isLoading}
                    sx={{ mt: 4}}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>{t('cancel')}</Button> {/* NEW: Use translation key for Cancel button */}
                <Button
                    onClick={handleDelete}
                    variant="contained"
                    color="error"
                    disabled={isLoading || !isConfirmationMatch}
                >
                    {isLoading ? <CircularProgress size={24} color="inherit" /> : t('delete_account_button')} {/* NEW: Use translation key for Delete button */}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteAccountDialog;